import { Image } from "@chakra-ui/image";
import { VStack, Box, HStack } from "@chakra-ui/react";
import { Listing } from "models/cards";
import { FeaturedAuction } from "models/featuredAuction";
import { FC, useEffect, useState } from "react";
import { centsToDollars } from "util/helpers";

type CarouselCardProps = {
    card: Listing | FeaturedAuction;
};

export const DashboardFeaturedCard = ({ card }: CarouselCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const isFeatured = "currentBid" in card;
    const hasPrice = isFeatured
        ? card.currentBid
        : card.salePriceInCents != null && card.salePriceInCents > 0;
    const hasEnded = isFeatured
        ? false
        : card.endingDate && new Date(card.endingDate) < new Date();
    const [timeLeft, setTimeLeft] = useState(isFeatured ? card.remaining : "");
    const [endingDate] = useState(isFeatured ? card.endDate : card.endingDate);
    const [endingShortly, setEndingShortly] = useState(false);

    const displayText = !hasPrice
        ? hasEnded
            ? "Recently Ended"
            : "Recently Listed"
        : isFeatured
          ? centsToDollars(card.currentBidInCents)
          : centsToDollars(card.salePriceInCents);

    useEffect(() => {
        if (hasEnded) {
            return;
        }
        setInterval(() => {
            // get total seconds between the times
            let delta =
                Math.abs(
                    new Date(endingDate).getTime() - new Date().getTime(),
                ) / 1000;

            // calculate (and subtract) whole days
            let days = Math.floor(delta / 86400);
            delta -= days * 86400;

            // calculate (and subtract) whole hours
            let hours = Math.floor(delta / 3600) % 24;
            delta -= hours * 3600;

            // calculate (and subtract) whole minutes
            let minutes = Math.floor(delta / 60) % 60;
            delta -= minutes * 60;

            let timeLeftString = "";
            if (days) {
                timeLeftString = `${days}d ${hours}h`;
            }

            if (hours && !days) {
                timeLeftString = `${hours}h ${minutes}m`;
                if (!hours && minutes < 15) {
                    timeLeftString = "Ending Shortly!";
                    setEndingShortly(true);
                }
            }

            setTimeLeft(timeLeftString);
        }, 60);
    }, [endingDate, hasEnded]);

    return (
        <Box
            style={{ flexShrink: 0 }}
            overflow="hidden"
            key={"currentBid" in card ? card.auctionUrl : card.sku}
            border="1px solid black"
            borderRadius="lg"
            backgroundColor="black"
            _hover={{ outline: "2px solid black" }}
            cursor="pointer"
        >
            <HStack spacing={0} alignItems="stretch">
                <VStack spacing={0}>
                    <Box
                        as="button"
                        onClick={() => {
                            setExpanded(true);
                        }}
                    >
                        <CardImage
                            imageUrl={
                                isFeatured ? card.imageUrl : card.frontImageUrl
                            }
                            ended={!!hasEnded}
                            timeLeft={timeLeft}
                            endingShortly={endingShortly}
                        />
                    </Box>

                    <Box
                        fontWeight={hasPrice ? "bold" : ""}
                        padding={2}
                        pt={2}
                        mt="-2"
                        backgroundColor="black"
                        textAlign="center"
                        color={!hasPrice ? "white" : "#16a34a"}
                        opacity={0.75}
                        width="100%"
                    >
                        {expanded ? "\u200b" : displayText}
                    </Box>
                </VStack>
                {expanded && (
                    <Box
                        backgroundColor="black"
                        color="white"
                        width={52}
                        position="relative"
                    >
                        <VStack
                            padding={4}
                            height="100%"
                            justifyContent="space-between"
                        >
                            <Box fontWeight="bold">{card.title}</Box>
                            <VStack spacing={0}>
                                <Box
                                    color={!hasPrice ? "white" : "#16a34a"}
                                    fontWeight="bold"
                                    fontSize={hasPrice ? "2xl" : "md"}
                                >
                                    {displayText}
                                </Box>

                                {!hasPrice && <Box>Check back soon!</Box>}
                            </VStack>
                            <VStack width="100%">
                                <VStack
                                    borderRadius={8}
                                    backgroundColor="gray.800"
                                    width="100%"
                                    alignItems="left"
                                    spacing={0}
                                >
                                    <Box color="lightgray">
                                        {hasEnded
                                            ? "Auction Ended"
                                            : "Auction Ends"}
                                        :
                                    </Box>
                                    <Box color="white">
                                        {new Date(
                                            endingDate,
                                        ).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                            year: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit",
                                            hour12: true,
                                        })}
                                    </Box>
                                </VStack>
                                {!hasEnded && (
                                    <Box
                                        borderRadius={8}
                                        color="white"
                                        as="button"
                                        width="100%"
                                        padding={2}
                                        bg="var(--dcs-blue)"
                                        onClick={() => {
                                            window.open(
                                                ("currentBid" in card
                                                    ? card.auctionUrl
                                                    : card.ebayItemUrl) ||
                                                    undefined,
                                            );
                                        }}
                                    >
                                        View on eBay
                                    </Box>
                                )}
                            </VStack>
                        </VStack>
                        <Box
                            backgroundColor="darkGray"
                            color="white"
                            position="absolute"
                            padding={2}
                            borderLeftRadius="lg"
                            right={0}
                            top="50%"
                            as="button"
                            onClick={() => {
                                setExpanded(false);
                            }}
                        >
                            {"<"}
                        </Box>
                    </Box>
                )}
            </HStack>
        </Box>
    );
};

type CardImageProps = {
    imageUrl: string;
    ended?: boolean;
    timeLeft?: string;
    endingShortly?: boolean;
};

const CardImage: FC<CardImageProps> = ({
    imageUrl,
    ended,
    timeLeft,
    endingShortly,
}) => {
    return (
        <Box
            width={192}
            borderRadius="lg"
            borderBottomRadius={0}
            height={64}
            overflow="hidden"
            position="relative"
        >
            <Image src={imageUrl} position="absolute" left={0} />
            {!ended && timeLeft && (
                <Box
                    position="absolute"
                    borderRadius="full"
                    backgroundColor="white"
                    color="black"
                    top={4}
                    right={4}
                    padding={1}
                    px={2}
                    border="1px solid white"
                    fontSize={12}
                    fontWeight="bold"
                >
                    {endingShortly ? `Ending Shortly!` : timeLeft}
                </Box>
            )}
        </Box>
    );
};
